import { createRoute } from '@tanstack/react-router';
import { lazy } from 'react';
import { z } from 'zod';
import { featureGate } from '../../router/gates';
import { appLayoutRoute } from '../../router/parents';
import { IntegrationsLayout } from './shared';

// Screens load on demand (React.lazy; the router renders each match inside Suspense).
const AiPage = lazy(() => import('./AiPage').then((m) => ({ default: m.AiPage })));
const ApiKeysPage = lazy(() => import('./DevPages').then((m) => ({ default: m.ApiKeysPage })));
const WebhooksPage = lazy(() => import('./DevPages').then((m) => ({ default: m.WebhooksPage })));
const CalendarPage = lazy(() => import('./EmailPages').then((m) => ({ default: m.CalendarPage })));
const EmailAccountsPage = lazy(() => import('./EmailPages').then((m) => ({ default: m.EmailAccountsPage })));
const InboxPage = lazy(() => import('./EmailPages').then((m) => ({ default: m.InboxPage })));
const StoragePage = lazy(() => import('./StoragePage').then((m) => ({ default: m.StoragePage })));
const OverviewPage = lazy(() => import('./OverviewPage').then((m) => ({ default: m.OverviewPage })));

/**
 * Integrations routes. `/integrations` is the sidebar entry; tabs are nested routes.
 * Hidden for the first release: while the `integrations` flag (lib/features.ts) is off, every
 * /integrations/* path redirects home (the OAuth return path lands there too).
 */
const integrationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/integrations',
  beforeLoad: featureGate('integrations'),
  component: IntegrationsLayout,
});

const overviewRoute = createRoute({
  getParentRoute: () => integrationsRoute,
  path: '/',
  component: OverviewPage,
});

const emailRoute = createRoute({
  getParentRoute: () => integrationsRoute,
  path: '/email',
  validateSearch: z.object({
    connected: z.string().max(40).optional(),
    error: z.string().max(60).optional(),
  }),
  component: function EmailRoute() {
    const { connected, error } = emailRoute.useSearch();
    return <EmailAccountsPage connected={connected} error={error} />;
  },
});

const inboxRoute = createRoute({
  getParentRoute: () => integrationsRoute,
  path: '/inbox',
  component: InboxPage,
});
const calendarRoute = createRoute({
  getParentRoute: () => integrationsRoute,
  path: '/calendar',
  component: CalendarPage,
});
const storageRoute = createRoute({ getParentRoute: () => integrationsRoute, path: '/storage', component: StoragePage });
const aiRoute = createRoute({ getParentRoute: () => integrationsRoute, path: '/ai', component: AiPage });
const webhooksRoute = createRoute({
  getParentRoute: () => integrationsRoute,
  path: '/webhooks',
  component: WebhooksPage,
});
const apiKeysRoute = createRoute({
  getParentRoute: () => integrationsRoute,
  path: '/api-keys',
  component: ApiKeysPage,
});

export const integrationsRoutes = [
  integrationsRoute.addChildren([
    overviewRoute,
    emailRoute,
    inboxRoute,
    calendarRoute,
    storageRoute,
    aiRoute,
    webhooksRoute,
    apiKeysRoute,
  ]),
] as const;
